// One rejected station action (plan §9.3, §10.4): what the station sent, who did it, when,
// and why the server refused it — in words, with the server's own note under "Technical
// detail" (review UI3A-2). Read-only: a rejection is final and nothing was recorded.

import { Badge, Button, LoadingSkeleton, PageHeader } from '@enp/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { ApiError } from '../../../../lib/api.js';
import { AccountDate } from '../../../../shell/account-date.js';
import { useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import { commandLabel, describeCommand, rejectReason } from '../../../../stations/labels.js';
import {
  DeviceName,
  NoDeviceAccess,
  SettingsBreadcrumb,
  SettingsFrame,
  TechnicalDetail,
  useCanManageDevices,
} from '../../../../stations/page-parts.js';
import { rejectedCommandQuery } from '../../../../stations/queries.js';
import { LATE_MS, durationText, lateBy } from '../../../../stations/words.js';

export const Route = createFileRoute('/_app/settings/rejected-actions/$commandId')({
  component: RejectedActionPage,
});

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[12rem_1fr]">
      <dt className="text-sm font-medium text-muted">{label}</dt>
      <dd className="text-body text-fg">{children}</dd>
    </div>
  );
}

function RejectedActionPage() {
  const { commandId } = Route.useParams();
  const canManage = useCanManageDevices();
  const q = useQuery({ ...rejectedCommandQuery(commandId), enabled: canManage === true });
  const item = q.data;
  const title = item ? commandLabel(item.type) : 'Rejected action';
  useDocumentTitle(title);

  const back = (
    <Button variant="secondary" asChild>
      <Link to="/settings/rejected-actions">
        <ArrowLeft aria-hidden />
        All rejected actions
      </Link>
    </Button>
  );

  if (canManage === false) {
    return (
      <SettingsFrame>
        <PageHeader title="Rejected action" />
        <NoDeviceAccess />
      </SettingsFrame>
    );
  }
  if (q.isError) {
    const notFound = q.error instanceof ApiError && q.error.status === 404;
    return (
      <SettingsFrame>
        {notFound ? (
          // Laid out as a found one (UI3A-213): the trail above, the way back below.
          <>
            <SettingsBreadcrumb
              trail={[{ to: '/settings/rejected-actions', label: 'Rejected station actions' }]}
              page="Not found"
            />
            <PageHeader
              title="This rejected action isn’t here"
              description="It may belong to another branch, or the link is wrong."
            />
            <div>{back}</div>
          </>
        ) : (
          <ErrorPanel
            error={q.error}
            what="This rejected action"
            onRetry={() => void q.refetch()}
            extraAction={back}
          />
        )}
      </SettingsFrame>
    );
  }
  if (!item) {
    return (
      <SettingsFrame>
        {/* A page has its h1 while loading too, as count detail does (UI8B-7). */}
        <h1 className="sr-only">Loading the rejected action</h1>
        <LoadingSkeleton variant="page" label="Loading the rejected action" />
      </SettingsFrame>
    );
  }

  const reason = rejectReason(item.reason);
  const late = lateBy(item.occurred_at, item.received_at);
  const technical = [item.reason, item.message].filter(Boolean).join(': ');
  return (
    <SettingsFrame>
      <SettingsBreadcrumb
        trail={[{ to: '/settings/rejected-actions', label: 'Rejected station actions' }]}
        page={title}
      />
      <PageHeader title={title} description={describeCommand(item.type, item.payload, item.refs)} />
      <div className="flex flex-col gap-6">
        {/* Final and nothing to do here: a neutral box, not an alarm (UI3A-209). */}
        <section
          aria-labelledby="why"
          className="rounded-lg border border-border bg-surface-sunken p-5"
        >
          <h2 id="why" className="font-display text-lg font-semibold text-fg">
            Why it was refused: {reason.label.toLowerCase()}
          </h2>
          <p className="mt-1 text-body text-fg">{reason.explain}</p>
          {/* When it happened and reached the server: once, in Details (B-041). */}
          <div className="mt-2">
            <TechnicalDetail>{technical}</TechnicalDetail>
          </div>
        </section>

        <section aria-labelledby="details" className="flex flex-col gap-3">
          <h2 id="details" className="font-display text-lg font-semibold text-fg">
            Details
          </h2>
          <dl className="flex flex-col gap-3">
            <Row label="Done by">
              {item.staff?.display_name ?? 'Someone this account doesn’t know'}
            </Row>
            <Row label="Device">
              <DeviceName code={item.device.device_code} station={item.device.station_name} />
            </Row>
            <Row label="Happened">
              <AccountDate value={item.occurred_at} withTime />
            </Row>
            <Row label="Reached the server">
              <AccountDate value={item.received_at} withTime />
              {late !== null &&
                (late > LATE_MS ? (
                  <Badge tone="warning" className="ml-2">
                    {durationText(late)} later
                  </Badge>
                ) : (
                  <span className="text-muted"> ({durationText(late)} later)</span>
                ))}
            </Row>
            <Row label="Station app">{item.app_version ?? 'Not stated'}</Row>
          </dl>
          <details className="rounded-lg border border-border p-4">
            <summary className="w-fit cursor-pointer rounded-sm text-body font-medium text-fg">
              What the station sent
            </summary>
            <pre className="mt-3 overflow-x-auto whitespace-pre-wrap break-all font-mono text-xs text-fg">
              {JSON.stringify(item.payload, null, 2)}
            </pre>
          </details>
        </section>
        <div>{back}</div>
      </div>
    </SettingsFrame>
  );
}
